// src/pages/WuduGuide.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const WuduGuide: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);

  const wuduSteps = [
    {
      step: 1,
      title: "Make Intention (Niyyah)",
      arabic: "نَوَيْتُ الوُضُوءَ لِرَفْعِ الحَدَثِ الأَصْغَرِ لِلّهِ تَعَالَى",
      transliteration: "Nawaytu al-wudu'a li-raf'i al-ḥadathi al-aṣghar lillāhi ta'ālā",
      translation: "I intend to perform ablution to remove minor impurity for the sake of Allah, the Most High.",
      instructions: "Make the intention in your heart silently. Intention is not spoken aloud.",
      note: "Note: Intention is made in the heart, not spoken aloud."
    },
    {
      step: 2,
      title: "Wash Hands",
      arabic: "يَغْسِلُ الكَفَّيْنِ ثَلَاثًا",
      transliteration: "Yagh-silu al-kaffayn thalāthan",
      translation: "Wash both hands up to the wrists three times.",
      instructions: "Begin by washing both hands thoroughly, starting with the right hand then left hand. Wash up to the wrists three times.",
      note: "Start with 'Bismillah' (In the name of Allah)"
    },
    {
      step: 3,
      title: "Rinse Mouth",
      arabic: "يَتَمَضْمَضُ ثَلَاثًا",
      transliteration: "Yatamaḍmaḍu thalāthan",
      translation: "Rinse the mouth three times with water.",
      instructions: "Take water in your right hand and rinse your mouth thoroughly three times. Swish water around and spit it out.",
      note: "Ensure water reaches all parts of the mouth"
    },
    {
      step: 4,
      title: "Clean Nose",
      arabic: "يَسْتَنْشِقُ وَيَسْتَنْثِرُ ثَلَاثًا",
      transliteration: "Yastanshiqu wa yastanthiru thalāthan",
      translation: "Sniff water into the nose and blow it out three times.",
      instructions: "Take water in your right hand, sniff it into your nose, then blow it out using your left hand. Repeat three times.",
      note: "Use left hand to clean the nose"
    },
    {
      step: 5,
      title: "Wash Face",
      arabic: "يَغْسِلُ الوَجْهَ ثَلَاثًا",
      transliteration: "Yagh-silu al-wajha thalāthan",
      translation: "Wash the face from forehead to chin and ear to ear three times.",
      instructions: "Wash your entire face three times, from hairline to chin and from ear to ear. Ensure all parts are covered.",
      note: "Include beard if present"
    },
    {
      step: 6,
      title: "Wash Arms",
      arabic: "يَغْسِلُ اليَدَ اليُمْنَى ثُمَّ اليُسْرَى إِلَى المِرْفَقِ ثَلَاثًا",
      transliteration: "Yagh-silu al-yad al-yumnā thumma al-yusrā ilā al-mirfaq thalāthan",
      translation: "Wash the right arm up to the elbow three times, then the left.",
      instructions: "Start with right arm, wash from fingertips to elbow three times. Then do the same with left arm.",
      note: "Start with right side first"
    },
    {
      step: 7,
      title: "Wipe Head",
      arabic: "يَمْسَحُ رَأْسَهُ مَرَّةً وَاحِدَةً",
      transliteration: "Yamsaḥu ra'sahu marrah wāḥidah",
      translation: "Wipe the head once (from the forehead to the back and return).",
      instructions: "Wet your hands and wipe your head from the forehead to the back, then back to the forehead. Do this once.",
      note: "This is done only once"
    },
    {
      step: 8,
      title: "Wipe Ears",
      arabic: "يَمْسَحُ الأُذُنَيْنِ مَعَ الرَّأْسِ",
      transliteration: "Yamsaḥu al-udhunayn ma'a al-ra's",
      translation: "Wipe the inside and outside of both ears with the same wet hands.",
      instructions: "Using your index fingers, wipe the inside of ears, and with thumbs wipe the back of ears. Use the same water from head wiping.",
      note: "Use same water from head wiping"
    },
    {
      step: 9,
      title: "Wash Feet",
      arabic: "يَغْسِلُ الرِّجْلَيْنِ إِلَى الكَعْبَيْنِ ثَلَاثًا",
      transliteration: "Yagh-silu ar-rijlayn ilā al-ka'bayn thalāthan",
      translation: "Wash both feet up to and including the ankles three times, starting with the right foot.",
      instructions: "Wash right foot first, ensuring water reaches between toes and covers up to ankles. Then wash left foot. Repeat three times.",
      note: "Start with right foot, ensure between toes"
    }
  ];

  const duaAfterWudu = {
    arabic: "أَشْهَدُ أَنْ لَا إِلٰهَ إِلَّا اللّٰهُ وَحْدَهُ لَا شَرِيكَ لَهُ، وَأَشْهَدُ أَنَّ مُحَمَّدًا عَبْدُهُ وَرَسُولُهُ\nاللَّهُمَّ اجْعَلْنِي مِنَ التَّوَّابِينَ، وَاجْعَلْنِي مِنَ الْمُتَطَهِّرِينَ",
    transliteration: "Ashhadu an lā ilāha illallāhu waḥdahu lā sharīka lah, wa ashhadu anna Muḥammadan 'abduhu wa rasūluh.\nAllāhummaj'alnī mina-t-tawwābīn, waj'alnī mina-l-mutaṭahhirīn.",
    translation: "I bear witness that there is no god but Allah, alone, without partner, and I bear witness that Muhammad is His servant and messenger.\nO Allah, make me among those who repent often and those who purify themselves.",
    reference: "Sahih Muslim (no. 234)"
  };

  const sunnahEtiquette = [
    "Begin with 'Bismillah' — بسم الله",
    "Follow the order exactly as the Prophet ﷺ did",
    "Perform each washing three times (unless once or twice suffices)",
    "Avoid wasting water",
    "Face the Qiblah if possible",
    "Maintain calmness and mindfulness — not rushing"
  ];

  const virtues = [
    {
      text: "When a Muslim performs Wudu and washes his face, every sin which his eyes committed is washed away with the water... until he emerges purified from sins.",
      reference: "Sahih Muslim, 244"
    },
    {
      text: "Whoever performs Wudu perfectly and says the dua after it, the eight gates of Jannah will be opened for him.",
      reference: "Sahih Muslim, 234"
    }
  ];

  const nextStep = () => {
    if (currentStep < wuduSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="section-padding">
      <div className="container">
        <div className="section-title">
          <h1>🌙 Complete Guide to Wudu (Ablution)</h1>
          <p>Step-by-step instructions for performing proper Islamic ablution</p>
        </div>

        {/* Step Navigation */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '32px',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          {wuduSteps.map((step, index) => (
            <button
              key={step.step}
              onClick={() => setCurrentStep(index)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: currentStep === index ? 'var(--accent-primary)' : 'var(--border)',
                backgroundColor: currentStep === index ? 'var(--accent-primary)' : 'transparent',
                color: currentStep === index ? 'var(--bg-primary)' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {step.step}
            </button>
          ))}
        </div>

        {/* Current Step Display */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <h2 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
              Step {wuduSteps[currentStep].step}: {wuduSteps[currentStep].title}
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              {wuduSteps[currentStep].instructions}
            </p>
          </div>

          {/* Arabic Text */}
          {wuduSteps[currentStep].arabic && (
            <div style={{ 
              backgroundColor: 'var(--bg-secondary)', 
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <p style={{ 
                fontSize: '24px', 
                lineHeight: '1.8',
                fontFamily: "'Amiri', 'Traditional Arabic', serif",
                direction: 'rtl',
                margin: 0
              }}>
                {wuduSteps[currentStep].arabic}
              </p>
            </div>
          )}

          {/* Transliteration and Translation */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              backgroundColor: 'var(--bg-secondary)', 
              padding: '16px',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'var(--accent-primary)', marginBottom: '8px' }}>Transliteration</h4>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                {wuduSteps[currentStep].transliteration}
              </p>
            </div>
            <div style={{ 
              backgroundColor: 'var(--bg-secondary)', 
              padding: '16px',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'var(--accent-primary)', marginBottom: '8px' }}>Translation</h4>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                {wuduSteps[currentStep].translation}
              </p>
            </div>
          </div>

          {wuduSteps[currentStep].note && (
            <div style={{ 
              backgroundColor: 'var(--accent-primary)',
              color: 'var(--bg-primary)',
              padding: '12px 16px',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              💡 {wuduSteps[currentStep].note}
            </div>
          )}

          {/* Navigation Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: '24px'
          }}>
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: currentStep === 0 ? 'var(--border)' : 'var(--accent-primary)',
                color: 'var(--bg-primary)',
                borderRadius: '8px',
                cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                opacity: currentStep === 0 ? 0.5 : 1
              }}
            >
              ← Previous Step
            </button>
            <button
              onClick={nextStep}
              disabled={currentStep === wuduSteps.length - 1}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: currentStep === wuduSteps.length - 1 ? 'var(--border)' : 'var(--accent-primary)',
                color: 'var(--bg-primary)',
                borderRadius: '8px',
                cursor: currentStep === wuduSteps.length - 1 ? 'not-allowed' : 'pointer',
                opacity: currentStep === wuduSteps.length - 1 ? 0.5 : 1
              }}
            >
              Next Step →
            </button>
          </div>
        </div>

        {/* Dua After Wudu */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '16px', textAlign: 'center' }}>
            📿 Dua After Wudu
          </h3>
          <div style={{ 
            backgroundColor: 'var(--bg-secondary)', 
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            <p style={{ 
              fontSize: '20px', 
              lineHeight: '1.8',
              fontFamily: "'Amiri', 'Traditional Arabic', serif",
              direction: 'rtl',
              margin: 0,
              whiteSpace: 'pre-line'
            }}>
              {duaAfterWudu.arabic}
            </p>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              backgroundColor: 'var(--bg-secondary)', 
              padding: '16px',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'var(--accent-primary)', marginBottom: '8px' }}>Transliteration</h4>
              <p style={{ color: 'var(--text-secondary)', margin: 0, whiteSpace: 'pre-line' }}>
                {duaAfterWudu.transliteration}
              </p>
            </div>
            <div style={{ 
              backgroundColor: 'var(--bg-secondary)', 
              padding: '16px',
              borderRadius: '8px'
            }}>
              <h4 style={{ color: 'var(--accent-primary)', marginBottom: '8px' }}>Translation</h4>
              <p style={{ color: 'var(--text-secondary)', margin: 0, whiteSpace: 'pre-line' }}>
                {duaAfterWudu.translation}
              </p>
            </div>
          </div>
          <div style={{ 
            color: 'var(--text-secondary)', 
            fontSize: '14px',
            textAlign: 'center',
            opacity: '0.7'
          }}>
            Reference: {duaAfterWudu.reference}
          </div>
        </div>

        {/* Sunnah Etiquette */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '16px', textAlign: 'center' }}>
            🌸 Sunnah Etiquette of Wudu
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '12px'
          }}>
            {sunnahEtiquette.map((etiquette, index) => (
              <div key={index} style={{ 
                backgroundColor: 'var(--bg-secondary)', 
                padding: '12px 16px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ color: 'var(--accent-primary)' }}>•</span>
                <span style={{ color: 'var(--text-secondary)' }}>{etiquette}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Virtues of Wudu */}
        <div className="card">
          <h3 style={{ color: 'var(--text-primary)', marginBottom: '16px', textAlign: 'center' }}>
            💎 Virtues of Wudu
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {virtues.map((virtue, index) => (
              <div key={index} style={{ 
                backgroundColor: 'var(--bg-secondary)', 
                padding: '16px',
                borderRadius: '8px'
              }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '8px', fontStyle: 'italic' }}>
                  "{virtue.text}"
                </p>
                <div style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '14px',
                  opacity: '0.7'
                }}>
                  {virtue.reference}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Back to Prayer Guide */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <Link 
            to="/learn-prayer" 
            className="btn-primary"
            style={{
              textDecoration: 'none',
              padding: '12px 24px',
              fontSize: '16px'
            }}
          >
            ← Back to Prayer Guide
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WuduGuide;